import api from './client';
import { Gender, BirdStatus, GenderDeterminationMethod, HealthStatus, VaccinationStatus, toApiBirdStatus } from '../utils/enums';
import { Species } from './species';
import { Photo } from './photos';

export interface Bird {
  id: string;
  bandId: string;
  speciesId: string;
  species?: Species;
  mutationName?: string;
  gender: Gender;
  genderDeterminationMethod: GenderDeterminationMethod;
  status: BirdStatus;
  sellPrice?: number;
  hatchingDate: Date;
  bandingDate: Date;
  cageNumber: string;
  avatarPhotoId?: string;
  provenance?: {
    type: 'BRED' | 'BOUGHT' | 'OTHER';
    fatherId?: string;
    motherId?: string;
    sellerName?: string;
    sellerContact?: string;
    sellerAddress?: string;
    dateAcquired?: Date;
    price?: number;
    notes?: string;
    purchaseOrderNumber?: string;
    invoiceNumber?: string;
    paymentMethod?: string;
    paymentReference?: string;
    transactionId?: string;
    purchaseId?: string;
    paymentId?: string;
    batchNumber?: string;
    warrantyInfo?: string;
    healthCertificateNumber?: string;
    quarantinePeriod?: number;
    quarantineStartDate?: Date;
    quarantineEndDate?: Date;
    saleInfo?: {
      dateSold?: Date;
      buyerName?: string;
      buyerContact?: string;
      buyerAddress?: string;
      salePrice?: number;
      saleInvoiceNumber?: string;
      salePaymentMethod?: string;
      salePaymentReference?: string;
      saleTransactionId?: string;
      saleId?: string;
      salePaymentId?: string;
      saleBatchNumber?: string;
      saleNotes?: string;
      deliveryDate?: Date;
      deliveryMethod?: string;
      trackingNumber?: string;
      transferCertificateNumber?: string;
      newOwnerLicense?: string;
    };
  };
  phenotype?: string;
  genotype?: string;
  parentIds: string[];
  siblingIds: string[];
  descendantIds: string[];
  photos?: Photo[];
  documents?: {
    id: string;
    url: string;
    type: string;
    dateAdded: Date;
  }[];
  notes?: {
    id: string;
    date: Date;
    heading: string;
    detail: string;
  }[];
  healthTests?: {
    id: string;
    testName: string;
    testeeName: string;
    testDate: Date;
    result: string;
    comment?: string;
    documents?: {
      id: string;
      url: string;
      type: string;
      dateAdded: Date;
    }[];
  }[];
  // Health-related fields
  healthStatus?: HealthStatus;
  vaccinationStatus?: VaccinationStatus;
  weight?: number;
  lastHealthCheck?: Date;
  nextHealthCheck?: Date;
  healthNotes?: string;
  currentMedications?: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateBirdDto {
  bandId: string;
  speciesId: string;
  mutationName?: string;
  gender: Gender;
  genderDeterminationMethod: GenderDeterminationMethod;
  status: BirdStatus;
  sellPrice?: number;
  hatchingDate: string;
  bandingDate: string;
  cageNumber: string;
  avatarPhotoId?: string;
  provenance: {
    type: 'BRED' | 'BOUGHT' | 'OTHER';
    fatherId?: string;
    motherId?: string;
    sellerName?: string;
    sellerContact?: string;
    sellerAddress?: string;
    dateAcquired?: string;
    price?: number;
    notes?: string;
    /** Links purchase to an existing supplier when creating the bird */
    supplierId?: string;
    purchaseOrderNumber?: string;
    invoiceNumber?: string;
    paymentMethod?: string;
    paymentReference?: string;
    transactionId?: string;
    purchaseId?: string;
    paymentId?: string;
    batchNumber?: string;
    warrantyInfo?: string;
    healthCertificateNumber?: string;
    quarantinePeriod?: number;
    quarantineStartDate?: string;
    quarantineEndDate?: string;
    saleInfo?: {
      dateSold?: string;
      buyerName?: string;
      buyerContact?: string;
      buyerAddress?: string;
      salePrice?: number;
      saleInvoiceNumber?: string;
      salePaymentMethod?: string;
      salePaymentReference?: string;
      saleTransactionId?: string;
      saleId?: string;
      salePaymentId?: string;
      saleBatchNumber?: string;
      saleNotes?: string;
      deliveryDate?: string;
      deliveryMethod?: string;
      trackingNumber?: string;
      transferCertificateNumber?: string;
      newOwnerLicense?: string;
    };
  };
  phenotype?: string;
  genotype?: string;
  genotypes?: Array<{ locusId: string; alleles: string[]; notes?: string }>;
  parentIds: string[];
  siblingIds: string[];
  descendantIds: string[];
  photos: {
    id: string;
    url: string;
    caption?: string;
    dateAdded: string;
  }[];
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const getBirds = async (): Promise<Bird[]> => {
  const pageSize = 100;
  const all: Bird[] = [];
  let page = 1;
  let totalPages = 1;

  do {
    const response = await api.get<PaginatedResponse<Bird> | Bird[]>('birds', {
      page: String(page),
      limit: String(pageSize),
    });

    if (!response?.data) break;

    if (Array.isArray(response.data)) {
      return response.data;
    }

    if (Array.isArray(response.data.items)) {
      all.push(...response.data.items);
      totalPages = Math.max(1, Number(response.data.totalPages) || 1);
      page += 1;
      continue;
    }

    break;
  } while (page <= totalPages);

  return all;
};

export const createBird = async (data: CreateBirdDto): Promise<Bird> => {
  const response = await api.post<Bird>('birds', sanitizeBirdWritePayload(data));
  return response.data;
};

export const updateBird = async (id: string, data: Partial<CreateBirdDto>): Promise<Bird> => {
  const response = await api.patch<Bird>(`birds/${id}`, sanitizeBirdWritePayload(data));
  return response.data;
};

/** Drop legacy / empty fields that production validation historically rejected. */
function sanitizeBirdWritePayload<T extends Partial<CreateBirdDto>>(data: T): T {
  const raw = { ...(data as Record<string, unknown>) };
  delete raw.documents;
  delete raw.notes;
  delete raw.healthTests;

  if (typeof raw.status === 'string') {
    raw.status = toApiBirdStatus(raw.status);
  }

  const cleanString = (v: unknown) =>
    typeof v === 'string' && v.trim() === '' ? undefined : v;

  for (const key of [
    'mutationName',
    'phenotype',
    'genotype',
    'cageNumber',
    'avatarPhotoId',
  ] as const) {
    if (key in raw) raw[key] = cleanString(raw[key]);
  }

  if (raw.sellPrice === undefined || raw.sellPrice === null || Number.isNaN(raw.sellPrice as number)) {
    delete raw.sellPrice;
  }

  if (Array.isArray(raw.genotypes)) {
    raw.genotypes = (raw.genotypes as Array<{ locusId?: string; alleles?: string[] }>)
      .filter((g) => g?.locusId && Array.isArray(g.alleles) && g.alleles.length > 0)
      .map((g) => ({ locusId: g.locusId, alleles: g.alleles }));
    if (!(raw.genotypes as unknown[]).length) delete raw.genotypes;
  }

  if (raw.provenance && typeof raw.provenance === 'object') {
    const provenance = { ...(raw.provenance as Record<string, unknown>) };
    for (const key of Object.keys(provenance)) {
      const value = provenance[key];
      if (value === '' || value === null) delete provenance[key];
    }
    raw.provenance = provenance;
  }

  raw.parentIds = Array.isArray(raw.parentIds) ? raw.parentIds : [];
  raw.siblingIds = Array.isArray(raw.siblingIds) ? raw.siblingIds : [];
  raw.descendantIds = Array.isArray(raw.descendantIds) ? raw.descendantIds : [];
  raw.photos = Array.isArray(raw.photos) ? raw.photos : [];

  return raw as T;
}

export const birdsApi = {
  getBirds: getBirds,
  getAll: () => api.get<Bird[]>('birds'),
  getById: (id: string) => api.get<Bird>(`birds/${id}`),
  create: (data: CreateBirdDto) => api.post<Bird>('birds', sanitizeBirdWritePayload(data)),
  update: (id: string, data: Partial<Bird>) =>
    api.patch<Bird>(`birds/${id}`, sanitizeBirdWritePayload(data as unknown as Partial<CreateBirdDto>)),
  delete: (id: string) => api.delete(`birds/${id}`),
};

export async function updateBirdAvatar(id: string, photoId: string): Promise<Bird> {
  const response = await api.patch<Bird>(`/birds/${id}/avatar`, { photoId });
  return response.data;
}

// Re-export BirdStatus for use in components
export { BirdStatus }; 